/**
 * Centralized API service for CAi backend.
 * Initialize once during app startup, then use throughout.
 */

export interface CaiSession {
  session_id: string;
  session_name: string | null;
  session_type: string | null;
  creation_time: string | null;
  is_active: boolean;
  is_favorite: boolean;
  username: string;
}

class CaiApiService {
  private apiUrl = "";
  private getToken: (() => Promise<string>) | null = null;

  /**
   * Must be called once during app init (before any API calls).
   */
  initialize(apiUrl: string, getToken: () => Promise<string>) {
    this.apiUrl = apiUrl;
    this.getToken = getToken;
  }

  get isInitialized() {
    return !!this.apiUrl && !!this.getToken;
  }

  private async request(
    path: string,
    options: RequestInit = {}
  ): Promise<Response> {
    if (!this.getToken) throw new Error("CaiApiService not initialized");

    const token = await this.getToken();
    const headers = new Headers(options.headers as HeadersInit);
    headers.set("Authorization", `Bearer ${token}`);
    if (!headers.has("Content-Type") && options.body && typeof options.body === "string") {
      headers.set("Content-Type", "application/json");
    }

    const response = await globalThis.fetch(`${this.apiUrl}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`API ${options.method ?? "GET"} ${path} failed (${response.status}): ${errorText}`);
    }

    return response;
  }

  // ─── Chat Sessions ───

  async createSession(sessionType: string): Promise<CaiSession> {
    const response = await this.request("/chat/sessions/create", {
      method: "POST",
      body: JSON.stringify({ session_type: sessionType }),
    });
    return response.json();
  }

  async deleteSession(sessionId: string, hardDelete = false): Promise<void> {
    await this.request(
      `/chat/sessions/delete?session_id=${encodeURIComponent(sessionId)}&hard_delete=${hardDelete}`,
      { method: "PUT" }
    );
  }

  async updateSessionName(sessionId: string, name: string): Promise<void> {
    const headers = new Headers();
    headers.set("Content-Type", "text/plain");
    await this.request(
      `/chat/sessions/updateSessionName?session_id=${encodeURIComponent(sessionId)}`,
      { method: "PUT", body: name, headers }
    );
  }

  async autoNameSession(sessionId: string): Promise<void> {
    await this.request(
      `/chat/sessions/updateSessionNameBasedOnHistory?session_id=${encodeURIComponent(sessionId)}`,
      { method: "PUT" }
    );
  }

  async getAllSessions(sessionTypes: string[]): Promise<CaiSession[]> {
    const params = sessionTypes.map((t) => `session_type=${encodeURIComponent(t)}`).join("&");
    const response = await this.request(`/chat/sessions/all?${params}`);
    return response.json();
  }

  // ─── Message Persistence (DynamoDB) ───

  async persistMessages(sessionId: string, messages: unknown[]): Promise<void> {
    await this.request(
      `/chat/sessions/${encodeURIComponent(sessionId)}/messages`,
      {
        method: "PUT",
        body: JSON.stringify(messages),
      }
    );
  }

  async loadMessages(sessionId: string): Promise<unknown[]> {
    const response = await this.request(
      `/chat/sessions/${encodeURIComponent(sessionId)}/messages`
    );
    return response.json();
  }

  // ─── Context (@ Mentions) ───

  async fetchAllManagers(): Promise<{ productId: string; productShortName: string }[]> {
    const response = await this.request("/context/managers/all");
    return response.json();
  }

  async fetchAllPersons(): Promise<{ email: string; display_name: string }[]> {
    const response = await this.request("/context/persons/all");
    return response.json();
  }

  async fetchUserKnowledgeBases(): Promise<{ agent_name: string; agent_description: string }[]> {
    const response = await this.request("/context/knowledge-bases");
    return response.json();
  }

  // ─── File Upload (RAG) ───

  async uploadFiles(sessionId: string, files: File[]): Promise<{ message: string }> {
    if (!this.getToken) throw new Error("CaiApiService not initialized");

    const token = await this.getToken();
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file);
    }

    const response = await fetch(
      `${this.apiUrl}/rag/uploadfiles?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`Upload failed (${response.status}): ${errorText}`);
    }

    return response.json().catch(() => ({ message: "Upload complete" }));
  }

  // ─── File Download (md_download) ───

  /**
   * Fetch a file from an md_download path.
   * Returns a blob URL that can be opened or used as an href.
   * Caches results for 10 minutes to avoid re-fetching.
   */
  private mdDownloadCache = new Map<string, { url: string; timestamp: number }>();
  private static MD_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

  async fetchMdDownload(mdPath: string): Promise<string> {
    // Strip leading slash if present
    const cleanPath = mdPath.startsWith("/") ? mdPath.slice(1) : mdPath;

    // Check cache
    const cached = this.mdDownloadCache.get(cleanPath);
    if (cached && Date.now() - cached.timestamp < CaiApiService.MD_CACHE_TTL) {
      return cached.url;
    }

    const response = await this.request(`/${cleanPath}`);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Revoke previous cached URL if exists
    if (cached) URL.revokeObjectURL(cached.url);
    this.mdDownloadCache.set(cleanPath, { url: blobUrl, timestamp: Date.now() });

    return blobUrl;
  }

  /**
   * Fetch a file from md_download and trigger a browser download.
   */
  async downloadMdFile(mdPath: string, filename?: string): Promise<void> {
    const blobUrl = await this.fetchMdDownload(mdPath);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename ?? mdPath.split("/").pop() ?? "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

export const caiApi = new CaiApiService();
